/**
 * Procedural SFX for study modes using Web Audio API.
 * No asset files required; volume should be 0–1 (game volume).
 */

let audioContext: AudioContext | null = null;

function getContext(): AudioContext | null {
  if (typeof window === "undefined") return null;
  if (!audioContext) {
    const AC = window.AudioContext || (window as Window & { webkitAudioContext?: typeof AudioContext }).webkitAudioContext;
    if (AC) audioContext = new AC();
  }
  return audioContext;
}

/** Resume the global AudioContext if suspended (required after user gesture in modern browsers). */
export function resumeAudioContext(): Promise<void> {
  const ctx = getContext();
  if (!ctx) return Promise.resolve();
  if (ctx.state === "suspended") return ctx.resume();
  return Promise.resolve();
}

/** Short "pop" for tapping a chip/word (soft click). */
export function playPop(volume = 0.4): void {
  const ctx = getContext();
  if (!ctx) return;
  const now = ctx.currentTime;
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  osc.connect(gain);
  gain.connect(ctx.destination);
  osc.type = "sine";
  osc.frequency.setValueAtTime(280, now);
  osc.frequency.exponentialRampToValueAtTime(180, now + 0.06);
  gain.gain.setValueAtTime(volume, now);
  gain.gain.exponentialRampToValueAtTime(0.001, now + 0.08);
  osc.start(now);
  osc.stop(now + 0.1);
}

/** Success chime (ascending two-note). */
export function playSuccess(volume = 0.35): void {
  const ctx = getContext();
  if (!ctx) return;
  const now = ctx.currentTime;
  const playTone = (freq: number, start: number, dur: number) => {
    const osc = ctx!.createOscillator();
    const gain = ctx!.createGain();
    osc.connect(gain);
    gain.connect(ctx!.destination);
    osc.type = "sine";
    osc.frequency.setValueAtTime(freq, start);
    gain.gain.setValueAtTime(0, start);
    gain.gain.linearRampToValueAtTime(volume, start + 0.02);
    gain.gain.exponentialRampToValueAtTime(0.001, start + dur);
    osc.start(start);
    osc.stop(start + dur);
  };
  playTone(523.25, now, 0.2);
  playTone(659.25, now + 0.12, 0.25);
}

/** Error buzz (harsh low tone). */
export function playError(volume = 0.3): void {
  const ctx = getContext();
  if (!ctx) return;
  const now = ctx.currentTime;
  const osc = ctx.createOscillator();
  const gain = ctx.createGain();
  const filter = ctx.createBiquadFilter();
  osc.connect(filter);
  filter.connect(gain);
  gain.connect(ctx.destination);
  osc.type = "sawtooth";
  osc.frequency.setValueAtTime(120, now);
  filter.type = "lowpass";
  filter.frequency.setValueAtTime(400, now);
  gain.gain.setValueAtTime(volume, now);
  gain.gain.exponentialRampToValueAtTime(0.001, now + 0.15);
  osc.start(now);
  osc.stop(now + 0.18);
}
